import { useEffect, useMemo, useRef, useState } from "react";

import { requestLoomDerivedSurface } from "../../api/loomDerivedSurfaceRequest";
import type { LoomVerifiedDerivedSurface } from "../../api/loomDerivedSurfaceAsset";
import type { LoomVerifiedAircraftTransitionAsset } from "./loomAircraftTransitionAsset";
import type { LoomOwnedDisplaySurface } from "./loomViewportPresentation";

interface Input { asset: LoomVerifiedAircraftTransitionAsset | null; documentId: string; projectId: string; sourceRevision: number | null; sourceStateId: string | null }
export interface LoomDerivedSurfaceState {
  accepted: LoomVerifiedDerivedSurface | null; error: string | null; staleDropCount: number;
  status: "unavailable" | "updating" | "accepted" | "error";
}
const EMPTY: LoomDerivedSurfaceState = { accepted: null, error: null, staleDropCount: 0, status: "unavailable" };
export function selectLoomDerivedPresentationSurface(primal: LoomOwnedDisplaySurface | null,
  adaptive: LoomDerivedSurfaceState): LoomOwnedDisplaySurface | null { return adaptive.accepted?.displaySurface ?? primal; }
function identity(input: Input): string | null {
  if (!input.asset || input.asset.header.version !== 3 || input.sourceRevision === null || !input.sourceStateId) return null;
  const header = input.asset.header;
  return [input.projectId, input.documentId, input.sourceRevision, input.sourceStateId, header.definitionBinding.definitionRevision,
    header.surface.geometryRevision, header.surface.topologyRevision, header.definitionBinding.assemblyTopologyRevision,
    header.definitionBinding.poseRevision, header.cacheReceipt.sceneRevision].join("\0");
}
export function useLoomDerivedSurface(input: Input): LoomDerivedSurfaceState {
  const requestIdentity = useMemo(() => identity(input), [input]);
  const inputRef = useRef(input); inputRef.current = input;
  const requestRef = useRef(0);
  const [entry, setEntry] = useState<{ identity: string | null; value: LoomDerivedSurfaceState }>({ identity: null, value: EMPTY });
  useEffect(() => {
    const sequence = ++requestRef.current; const controller = new AbortController(); const current = inputRef.current;
    if (!requestIdentity || !current.asset || current.sourceRevision === null || !current.sourceStateId) {
      setEntry((previous) => ({ identity: null, value: { ...EMPTY, staleDropCount: previous.value.staleDropCount } }));
      return () => controller.abort();
    }
    setEntry((previous) => ({ identity: requestIdentity, value: { accepted: previous.identity === requestIdentity ? previous.value.accepted : null,
      error: null, staleDropCount: previous.value.staleDropCount, status: "updating" } }));
    void requestLoomDerivedSurface({ asset: current.asset, documentId: current.documentId, projectId: current.projectId,
      sourceRevision: current.sourceRevision, sourceStateId: current.sourceStateId, signal: controller.signal }).then((accepted) => {
      if (sequence !== requestRef.current || controller.signal.aborted) {
        setEntry((previous) => ({ ...previous, value: { ...previous.value, staleDropCount: previous.value.staleDropCount + 1 } })); return;
      }
      setEntry((previous) => ({ identity: requestIdentity, value: { accepted, error: null,
        staleDropCount: previous.value.staleDropCount, status: "accepted" } }));
    }).catch((reason: unknown) => {
      if (sequence !== requestRef.current || controller.signal.aborted) return;
      setEntry((previous) => ({ identity: requestIdentity, value: { accepted: previous.identity === requestIdentity ? previous.value.accepted : null,
        error: reason instanceof Error ? reason.message : "Surface refinement failed", staleDropCount: previous.value.staleDropCount, status: "error" } }));
    });
    return () => controller.abort();
  }, [requestIdentity]);
  if (!requestIdentity) return { ...EMPTY, staleDropCount: entry.value.staleDropCount };
  if (entry.identity !== requestIdentity) return { ...EMPTY, status: "updating", staleDropCount: entry.value.staleDropCount };
  return entry.value;
}
